import { useState, useRef, useCallback, useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { APIProvider } from '@vis.gl/react-google-maps'
import { toast } from 'sonner'
import '~/styles/recs.css'
import eventsEmptyStateImage from '~/Images/events-empty-state.png'
import {
  PURPLE,
  PURPLE_MID,
  PURPLE_LIGHT,
  EVENT_TYPES,
  DISTANCES,
  SUPPORTED_CITIES,
  getCityTier,
  isAreaPlace,
  isBroaderThanCity,
  detectCityFromPlace,
  getLocationBoundaryValidationError,
  getVenueGroupNameForLocation,
} from '~/client/components/recs/constants'
import { useSpaces } from '~/client/space/use-spaces.hook'
import { TransitionScreen } from '~/client/components/recs/TransitionScreen'
import { UnsupportedCityAlert } from '~/client/components/recs/UnsupportedCityAlert'
import { seo } from '~/client/common/seo'
import { useEvents } from '~/client/event/use-events.hook'
import { useShareableLists } from '~/client/shareable-list/use-shareable-lists.hook'
import { ShareableListDialog } from '~/client/components/shareable-list/ShareableListDialog'
import { CollectionsHomeTable } from '~/client/components/shareable-list/CollectionsHomeTable'
import { AddEventToSpaceDialog } from '~/client/components/event/EventCreationDialogs'
import { useCreateEvent } from '~/client/event/use-create-event.hook'
import { useEffectiveUserDetail } from '~/client/common/stores/auth'
import { useListingsStore } from '~/client/common/stores/listings'
import { formatUtcDateShortFromUnknown } from '~/shared/common/date'
import { hasFeature, FeatureFlag } from '~/client/common/features'
import {
  GoogleMapsAutocomplete,
  useGoogleMapsAutocomplete,
} from '~/client/components/ui/google-maps-autocomplete'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/client/components/ui/select'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationEllipsis,
} from '~/client/components/ui/pagination'

export const Route = createFileRoute('/_app/')({
  component: RecsHomeComponent,
  head: () => ({
    meta: seo({
      title: 'Revel Street | Home',
      description: 'AI-powered venue intelligence for corporate event planners. Find the right space, every time.',
    }),
  }),
})

// ── Distance label helper ───────────────────────────────────────────────────

const DISTANCE_LABELS: Record<string, string> = {
  '0.5 mi': '~10 min walk',
  '1 mi': '',
  '5 mi': '',
  '10 mi': '',
}

// ── Distance → miles number ─────────────────────────────────────────────────

function distanceToMiles(d: string): number {
  const n = parseFloat(d)
  return isNaN(n) ? 1 : n
}



// ── Component ───────────────────────────────────────────────────────────────

function RecsHomeComponent() {
  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <RecsHomeInner />
    </APIProvider>
  )
}

function RecsHomeInner() {
  const navigate = useNavigate()
  const createEventMutation = useCreateEvent()
  const { data: events, isLoading: eventsLoading } = useEvents({ page_size: 200 })
  // useEffectiveUserDetail: during impersonation returns the target's full User,
  // otherwise returns the admin's user. Drives greeting, venue group scoping,
  // and feature gating — all of which should reflect the user being viewed.
  const effectiveUser = useEffectiveUserDetail()
  const hasCollectionsFeature = hasFeature(effectiveUser, FeatureFlag.COLLECTIONS)
  const { data: collections, isLoading: collectionsLoading } = useShareableLists({ enabled: hasCollectionsFeature })
  const resetFilters = useListingsStore((s) => s.resetFilters)

  const firstName = effectiveUser?.first_name || 'there'
  const venueGroups = effectiveUser?.active_entity?.venue_groups ?? []
  const entityCityNames = useMemo(() => venueGroups.map((vg: { name: string }) => vg.name), [venueGroups])

  // ── Form state ──────────────────────────────────────────────────────────
  const { value: locationInput, onChange: handleGoogleChange, placeDetails } = useGoogleMapsAutocomplete()
  const [detectedCity, setDetectedCity] = useState<string | null>(null)
  const [cityStatus, setCityStatus] = useState<'supported' | 'unsupported' | 'too_broad' | null>(null)
  const [distance, setDistance] = useState('0.5 mi')
  const [isLocationArea, setIsLocationArea] = useState(false)
  const [guests, setGuests] = useState('')
  const [privacy, setPrivacy] = useState('')
  const [eventType, setEventType] = useState('')
  const [eventName, setEventName] = useState('')
  const [hoveredRow, setHoveredRow] = useState<number | null>(null)

  const [isCreateCollectionOpen, setIsCreateCollectionOpen] = useState(false)
  const [isCreateEventOpen, setIsCreateEventOpen] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [formTouched, setFormTouched] = useState(false)
  const [guestsError, setGuestsError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const eventsRef = useRef<HTMLDivElement>(null)

  // ── Resolve venue group from detected city for real space count ────────
  const matchedVenueGroupId = detectedCity
    ? venueGroups.find((vg) => vg.name.toLowerCase() === detectedCity.toLowerCase())?.id
    : undefined
  const { count: realSpaceCount } = useSpaces({
    active_venue_group_id: matchedVenueGroupId ?? 0,
    page: 1,
  })

  // ── Pagination for My Events table ────────────────────────────────────
  const EVENTS_PAGE_SIZE = 10
  const totalPages = Math.max(1, Math.ceil((events?.length ?? 0) / EVENTS_PAGE_SIZE))
  const safePage = Math.min(currentPage, totalPages)
  const paginatedEvents = (events ?? []).slice(
    (safePage - 1) * EVENTS_PAGE_SIZE,
    safePage * EVENTS_PAGE_SIZE,
  )
  const pageList = useMemo(() => {
    const pages: (number | 'ellipsis')[] = []
    if (totalPages <= 7) {
      for (let p = 1; p <= totalPages; p++) pages.push(p)
      return pages
    }
    pages.push(1)
    const left = Math.max(2, safePage - 1)
    const right = Math.min(totalPages - 1, safePage + 1)
    if (left > 2) pages.push('ellipsis')
    for (let p = left; p <= right; p++) pages.push(p)
    if (right < totalPages - 1) pages.push('ellipsis')
    pages.push(totalPages)
    return pages
  }, [safePage, totalPages])

  // ── Transition screen state ───────────────────────────────────────────
  const [showTransition, setShowTransition] = useState(false)
  const [transitionCity, setTransitionCity] = useState('')
  const [transitionVenueCount, setTransitionVenueCount] = useState(0)
  // We store the pending navigation so we can redirect after the transition.
  // Both the API call and the animation run in parallel. We redirect as soon as BOTH
  // have completed. The ref tracks the nav object (set by API callback) and
  // a boolean flag tracks whether the animation has finished.
  const pendingNavRef = useRef<{ slug: string; search: Record<string, any> } | null>(null)
  const transitionDoneRef = useRef(false)

  const isSupported = cityStatus === 'supported'
  const isUnsupported = cityStatus === 'unsupported'
  const isToooBroad = cityStatus === 'too_broad'
  const formReady = isSupported && guests && !guestsError && eventType && eventName.trim()

  // ── Handle place selection from Google Maps ───────────────────────────
  const handlePlaceSelect = (placeDetails: any) => {
    // Reject places broader than a city (states, countries, etc.)
    if (isBroaderThanCity(placeDetails?.types)) {
      setDetectedCity(null)
      setCityStatus('too_broad')
      setIsLocationArea(true)
      return
    }
    const lat = placeDetails?.geometry?.location.lat()
    const lng = placeDetails?.geometry?.location.lng()
    const boundaryMatchedCity = getVenueGroupNameForLocation({ lat, lng, venueGroups })
    const nameMatchedCity = detectCityFromPlace(placeDetails)
    const matchedCity = boundaryMatchedCity ?? nameMatchedCity
    if (matchedCity) {
      setDetectedCity(matchedCity)
      setCityStatus('supported')
    } else {
      setDetectedCity(null)
      setCityStatus('unsupported')
    }
    setIsLocationArea(isAreaPlace(placeDetails?.types))
  }

  const handleLocationInputChange = (value: string, placeDetails?: any) => {
    handleGoogleChange(value, placeDetails)
    if (!placeDetails) {
      // User is still typing, reset city detection
      setDetectedCity(null)
      setCityStatus(null)
      setIsLocationArea(false)
    }
  }

  const resetLocation = () => {
    handleGoogleChange('')
    setDetectedCity(null)
    setCityStatus(null)
    setIsLocationArea(false)
  }

  // ── Shared field style ──────────────────────────────────────────────────
  const fieldStyle = (hasValue: boolean, isInvalid?: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${isInvalid ? '#FCA5A5' : hasValue ? '#C4B5FD' : '#E5E7EB'}`,
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'inherit',
    color: '#111',
    background: '#fff',
    boxSizing: 'border-box' as const,
    transition: 'all 0.15s',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
  })

  // ── Called when the TransitionScreen animation finishes ────────────────
  const handleTransitionDone = useCallback(() => {
    transitionDoneRef.current = true
    // If API already finished, navigate now
    const nav = pendingNavRef.current
    if (nav) {
      pendingNavRef.current = null
      resetFilters()
      navigate({
        to: '/events/$eventSlug',
        params: { eventSlug: nav.slug },
        search: nav.search,
      })
    }
    // Otherwise the API callback will navigate when it completes
  }, [navigate, resetFilters])

  // ── Helper: redirect if both animation & API are done ─────────────────
  const navigateIfReady = useCallback(() => {
    if (transitionDoneRef.current && pendingNavRef.current) {
      const nav = pendingNavRef.current
      pendingNavRef.current = null
      resetFilters()
      navigate({
        to: '/events/$eventSlug',
        params: { eventSlug: nav.slug },
        search: nav.search,
      })
    }
  }, [navigate, resetFilters])

  // ── "Find my venue" — show transition, create event in parallel ───────
  const handleFindVenue = async () => {
    if (!formReady) {
      setFormTouched(true)
      return
    }

    const city = detectedCity || 'Chicago'
    const tier = getCityTier(city)
    const venueCount = realSpaceCount ?? SUPPORTED_CITIES[city]?.venueCount ?? 0
    const lat = placeDetails?.geometry?.location.lat() ?? undefined
    const lng = placeDetails?.geometry?.location.lng() ?? undefined

    if (!tier) return

    const locationBoundaryError = getLocationBoundaryValidationError({ lat, lng, venueGroups })
    if (locationBoundaryError) {
      toast.error(locationBoundaryError, { position: 'bottom-right' })
      setFormTouched(true)
      return
    }

    setIsCreating(true)

    const isPremium = tier === 'premium'

    // ── For essential cities, show the "Scanning {city}" transition ────
    if (!isPremium) {
      transitionDoneRef.current = false
      pendingNavRef.current = null
      setTransitionCity(city)
      setTransitionVenueCount(venueCount)
      setShowTransition(true)
    }

    // ── Create the event ──────────────────────────────────────────────
    try {
      const result = await createEventMutation.mutateAsync({
        payload: {
          name: eventName.trim(),
          location: locationInput,
          location_max_distance: isLocationArea ? null : distanceToMiles(distance),
          latitude: lat ?? null,
          longitude: lng ?? null,
          num_attendees: parseInt(guests, 10) || null,
          event_type: eventType || null,
          privacy_requirement: privacy || null,
          support_mode: tier === 'essential' ? 'revel_recs' : 'self_service',
          venue_group_id: matchedVenueGroupId ?? null,
        },
        shouldAddSpaceOnCreate: false,
      })

      const createdEventId = result.event?.id
      const createdSlug = result.event?.slug

      if (!createdEventId) {
        toast.error('Failed to create event. Please try again.')
        setIsCreating(false)
        setShowTransition(false)
        return
      }

      // Build search params with all filter data baked in, then navigate.
      // resetFilters() disables "search as map moves" so initial filters aren't poisoned.
      const slug = createdSlug || String(createdEventId)

      const guestsNum = parseInt(guests, 10) || undefined
      let standingGuestCount: number | undefined
      let seatedGuestCount: number | undefined
      if (guestsNum && eventType) {
        if (eventType === 'Cocktail Reception + Dinner') {
          seatedGuestCount = Math.round(guestsNum * 1.75)
        } else if (['Cocktail Reception', 'Happy Hour', 'Holiday Party'].includes(eventType)) {
          standingGuestCount = guestsNum
        } else if (['Dinner', 'Meeting', 'Meeting with Breakout Spaces', 'Breakfast', 'Lunch'].includes(eventType)) {
          seatedGuestCount = guestsNum
        }
      }

      let privacyTypesParam: string | undefined
      switch (privacy) {
        case 'Private':
          privacyTypesParam = 'Practically Private,Buyout,Private'
          break
        case 'Semi-Private':
          privacyTypesParam = 'Buyout,Semi-Private,Main Dining Area'
          break
        case 'Buyout':
          privacyTypesParam = 'Buyout'
          break
        default:
          privacyTypesParam = undefined
      }

      const searchParams = {
        fromCreate: true,
        location: locationInput || undefined,
        locationLat: lat,
        locationLng: lng,
        radiusMiles: isLocationArea ? undefined : distanceToMiles(distance),
        standingGuestCount,
        seatedGuestCount,
        privacyTypes: privacyTypesParam,
      }

      if (isPremium) {
        // Premium cities skip the transition — navigate directly
        resetFilters()
        navigate({
          to: '/events/$eventSlug',
          params: { eventSlug: slug },
          search: searchParams,
        })
      } else {
        // Essential cities — wait for both animation & API to finish
        pendingNavRef.current = { slug, search: searchParams }
        navigateIfReady()
      }
    } catch (err) {
      console.error('Event creation failed:', err)
      toast.error('Something went wrong creating your event.')
      setIsCreating(false)
      setShowTransition(false)
    }
  }

  // ── Navigate to collection ──────────────────────────────────────────────
  const handleCollectionClick = (slug: string) => {
    navigate({ to: '/collection/$slug', params: { slug } })
  }

  // ── Navigate to existing event ──────────────────────────────────────────
  const handleEventClick = (event: any) => {
    navigate({
      to: '/events/$eventSlug',
      params: { eventSlug: event.slug || String(event.id) },
      search: {
        tab: 'shortlist',
      },
    })
  }

  // ── Helpers for My Events table ─────────────────────────────────────────
  const formatDate = (event: any): string => {
    const firstDate = event?.event_space_list_dates?.[0]?.start_date
    if (!firstDate) return '—'
    try {
      return formatUtcDateShortFromUnknown(firstDate) ?? '—'
    } catch {
      return '—'
    }
  }

  const getGuestCount = (event: any): string => {
    return event?.num_attendees ? event.num_attendees.toLocaleString() : '—'
  }

  const OWNER_NAME_MAX_LENGTH = 18
  const getOwner = (event: any): string => {
    const author = event?.update_author
    if (!author) return '—'
    const first = author.first_name?.trim() || ''
    const last = author.last_name?.trim() || ''
    const full = [first, last].filter(Boolean).join(' ')
    if (!full) return author.email || '—'
    if (full.length <= OWNER_NAME_MAX_LENGTH) return full
    if (first && last) return `${first} ${last[0]}.`
    return full
  }

  const getLocation = (event: any): string => {
    return event?.venue_group_name || '—'
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-full overflow-y-auto bg-[#FEFBF6] font-[Inter,system-ui,sans-serif]">
      {/* ── Full-screen "Scanning {city}" transition overlay ────────────── */}
      {showTransition && (
        <TransitionScreen
          eventName={eventName}
          city={transitionCity}
          guests={parseInt(guests, 10) || 50}
          venueCount={transitionVenueCount}
          onDone={handleTransitionDone}
        />
      )}
      <div className="mx-auto max-w-[960px] px-8 pt-16">
        {/* ── Welcome ──────────────────────────────────────────────────────── */}
        <div className="mb-9" style={{ animation: 'recs-fadeUp 0.4s ease both' }}>
          <div className="mb-1.5 text-sm font-medium text-gray-400">
            Welcome back, {firstName}
          </div>
          <h1 className="m-0 font-serif text-[40px] font-semibold leading-none tracking-normal text-gray-900">
            Let's find that perfect venue
          </h1>
        </div>

        {/* ── Event Creation Form Card ─────────────────────────────────────── */}
        <div
          className="rounded-[14px] border border-[#E9E8E3] bg-white px-7 pb-6 pt-7"
          style={{
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            animation: 'recs-fadeUp 0.4s ease 0.05s both',
          }}
        >
          {/* Location + Distance row */}
          <div className="mb-5 flex items-start gap-4">
            <div className="flex-1">
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold tracking-tight text-gray-700">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                Find a venue near
              </label>
              <GoogleMapsAutocomplete
                value={locationInput}
                onChange={handleLocationInputChange}
                onPlaceSelect={handlePlaceSelect}
                placeholder="(e.g. Javits Center, 375 Park Ave)"
                className={`recs-input !h-auto !rounded-lg !border !px-3 !py-2.5 !pl-9 !text-sm ${isUnsupported || isToooBroad
                  ? '!border-red-300'
                  : isSupported
                    ? '!border-violet-300'
                    : '!border-gray-200'
                  }`}
              />

              {/* ── Supported city badge (purple check) ──────────────────── */}
              {isSupported && detectedCity && (
                <div
                  className="mt-1.5 inline-flex items-center gap-1 rounded-full px-[9px] py-[3px] text-[11px] font-semibold"
                  style={{
                    background: PURPLE_LIGHT,
                    color: PURPLE,
                    animation: 'recs-fadeIn 0.2s ease both',
                  }}
                >
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
                    <polyline
                      points="20 6 9 17 4 12"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {detectedCity}
                </div>
              )}

              {/* ── Too-broad location error ────────────────────────────── */}
              {isToooBroad && (
                <div
                  className="mt-2 rounded-[10px] border border-orange-200 bg-orange-50 px-4 py-3.5"
                  style={{ animation: 'recs-fadeIn 0.2s ease both' }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="mb-0.5 text-[13px] font-semibold text-orange-900">
                        That's a bit too broad
                      </div>
                      <div className="text-xs leading-relaxed text-orange-700">
                        Please enter a specific city or address so we can find nearby venues.
                      </div>
                    </div>
                    <button
                      onClick={resetLocation}
                      className="border-none bg-transparent text-lg leading-none text-orange-700 hover:opacity-70"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              {/* ── Unsupported city error (orange card) ─────────────────── */}
              {isUnsupported && (
                <UnsupportedCityAlert
                  cityName={locationInput.split(',')[0]?.trim()}
                  onDismiss={resetLocation}
                  entityCityNames={entityCityNames}
                />
              )}
            </div>

            {/* ── Within (distance chips) ────────────────────────────────── */}
            {isSupported && !isLocationArea && (
              <div className="flex-shrink-0">
                <label className="mb-1.5 block text-xs font-semibold tracking-tight text-gray-700">
                  Within
                </label>
                <div className="flex gap-1.5 mt-[-1.5px]">
                  {DISTANCES.map((d) => (
                    <button
                      key={d}
                      onClick={() => setDistance(d)}
                      className="cursor-pointer whitespace-nowrap rounded-lg px-3 py-2 font-inherit text-xs font-medium leading-tight transition-all duration-150"
                      style={{
                        border: `1px solid ${distance === d ? PURPLE : '#E5E7EB'}`,
                        background: distance === d ? PURPLE_LIGHT : '#fff',
                        color: distance === d ? PURPLE : '#6B7280',
                        minHeight: 42,
                      }}
                    >
                      {d}
                      {DISTANCE_LABELS[d] && (
                        <>
                          <br />
                          <span className="flex items-center gap-[2px] text-[10px] opacity-70">
                            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M3.75 2.75C4.3 2.75 4.75 2.3 4.75 1.75C4.75 1.2 4.3 0.75 3.75 0.75C3.2 0.75 2.75 1.2 2.75 1.75C2.75 2.3 3.2 2.75 3.75 2.75ZM1.9 4.45L0.62 10.905C0.555 11.21 0.795001 11.5 1.11 11.5H1.15C1.385 11.5 1.585 11.34 1.64 11.11L2.45 7.5L3.5 8.5V11C3.5 11.275 3.725 11.5 4 11.5C4.275 11.5 4.5 11.275 4.5 11V8.18C4.5 7.905 4.39 7.645 4.19 7.455L3.45 6.75L3.75 5.25C4.31343 5.89419 5.0848 6.32056 5.93 6.455C6.23 6.5 6.5 6.26 6.5 5.955C6.5 5.71 6.32 5.505 6.075 5.465C5.315 5.34 4.685 4.89 4.35 4.3L3.85 3.5C3.71497 3.28408 3.50751 3.12325 3.26475 3.0463C3.02199 2.96935 2.75976 2.9813 2.525 3.08L0.61 3.89C0.428856 3.96743 0.274462 4.09638 0.166009 4.26084C0.0575563 4.4253 -0.000171843 4.618 3.84247e-007 4.815V6C3.84247e-007 6.275 0.225 6.5 0.5 6.5C0.775 6.5 1 6.275 1 6V4.8L1.9 4.45Z"
                                fill="currentColor"
                              />
                            </svg>
                            {DISTANCE_LABELS[d]}
                          </span>
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Progressive fields (animate in once valid city confirmed) ── */}
          <div
            className="overflow-hidden transition-all duration-300"
            style={{
              maxHeight: isSupported ? 500 : 0,
              opacity: isSupported ? 1 : 0,
            }}
          >
            <div className="flex flex-col gap-4 pt-5">
              {/* # of Guests + Type of Event */}
              <div className="grid grid-cols-[140px_1fr] gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold tracking-tight text-gray-700">
                    # of Guests
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={guests}
                    onChange={(e) => {
                      const val = e.target.value
                      setGuests(val)
                      if (val && !/^\d+$/.test(val.trim())) {
                        setGuestsError('Please enter a whole number')
                      } else if (val && parseInt(val.trim(), 10) < 1) {
                        setGuestsError('Must be at least 1')
                      } else {
                        setGuestsError(null)
                      }
                    }}
                    placeholder="e.g. 50"
                    className="recs-input"
                    style={fieldStyle(!!guests, !!guestsError)}
                  />
                  {guestsError && (
                    <div className="mt-1 text-[11px] text-red-500">{guestsError}</div>
                  )}
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold tracking-tight text-gray-700">
                    Type of Event
                  </label>
                  <Select value={eventType} onValueChange={setEventType}>
                    <SelectTrigger
                      className={`!h-auto !rounded-lg !py-2.5 !text-sm ${eventType ? '!border-violet-300' : '!border-gray-200'
                        }`}
                    >
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>



              {/* Event Name (required) */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold tracking-tight text-gray-700">
                  Event Name
                </label>
                <input
                  value={eventName}
                  onChange={(e) => {
                    setEventName(e.target.value)
                    setFormTouched(true)
                  }}
                  placeholder="e.g. Q4 Leadership Dinner"
                  className="recs-input"
                  style={fieldStyle(!!eventName, formTouched && !eventName.trim())}
                />
                {formTouched && !eventName.trim() && (
                  <div className="mt-1 text-[11px] text-red-500">Event name is required</div>
                )}
              </div>
            </div>
          </div>

          {/* ── CTA ────────────────────────────────────────────────────────── */}
          <div className="mt-5 flex justify-end pt-5">
            <button
              onClick={handleFindVenue}
              disabled={isCreating}
              className="rounded-[9px] border-none px-7 py-[11px] font-inherit text-sm font-semibold transition-all duration-150"
              style={{
                background: isCreating ? PURPLE_MID : formReady ? PURPLE : PURPLE_LIGHT,
                color: formReady || isCreating ? '#fff' : '#C4B5FD',
                cursor: formReady && !isCreating ? 'pointer' : 'default',
                opacity: isCreating ? 0.8 : 1,
              }}
              onMouseEnter={(e) => {
                if (formReady && !isCreating) {
                  e.currentTarget.style.background = PURPLE_MID
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(107,33,232,0.3)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isCreating) {
                  e.currentTarget.style.background = formReady ? PURPLE : PURPLE_LIGHT
                  e.currentTarget.style.boxShadow = 'none'
                }
              }}
            >
              {isCreating ? (
                <span className="flex items-center gap-2">
                  <span
                    className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white"
                  />
                  Creating event...
                </span>
              ) : (
                'Find my venue'
              )}
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            My Events Table (Section 5.3)
            ═══════════════════════════════════════════════════════════════════ */}
        <div
          ref={eventsRef}
          className="mt-12"
          style={{ animation: 'recs-fadeUp 0.4s ease 0.1s both' }}
        >
          <div className="mb-[18px] flex items-center justify-between">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">My Events</h2>
          </div>

          {eventsLoading ? (
            /* Skeleton loading */
            (<div className="overflow-hidden rounded-[14px] border border-[#E9E8E3] bg-white">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-6 border-b border-[#F3F3F0] px-5 py-4"
                >
                  <div
                    className="h-4 rounded bg-gray-100"
                    style={{
                      width: 160,
                      animation: `recs-barShimmer 1.8s ease-in-out ${i * 0.15}s infinite`,
                      backgroundSize: '400px 100%',
                      background:
                        'linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)',
                    }}
                  />
                  <div
                    className="h-3 rounded bg-gray-100"
                    style={{ width: 90 }}
                  />
                  <div
                    className="h-3 rounded bg-gray-100"
                    style={{ width: 110 }}
                  />
                  <div
                    className="h-3 rounded bg-gray-100"
                    style={{ width: 40 }}
                  />
                </div>
              ))}
            </div>)
          ) : events.length === 0 ? (
            /* Empty state */
            (<div className="rounded-[14px] border border-[#E9E8E3] bg-white px-8 py-14 text-center">
              <img src={eventsEmptyStateImage} alt="" className="mx-auto mb-5 h-24 w-auto" />
              <div className="mb-1 text-sm font-bold text-gray-900">No events yet</div>
              <div className="mb-5 text-[13px] text-gray-400">Start your search by creating an event</div>
              <button
                onClick={() => setIsCreateEventOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#7c3aed] px-4 py-2 text-sm font-semibold text-white hover:bg-[#6d28d9]"
              >
                + Create Event
              </button>
            </div>)
          ) : (
            /* Events table */
            (<div
              className="flex flex-col overflow-hidden rounded-[14px] border border-[#E9E8E3] bg-white"
              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            >
              <div className="flex-1" style={{ minHeight: 5 * 52 + 41 }}>
                <table className="w-full table-fixed border-collapse">
                  <thead>
                    <tr className="border-b border-[#E9E8E3] bg-[#F9F8FF]">
                      {[
                        { label: 'Event Name', width: '32%' },
                        { label: 'Date', width: '16%' },
                        { label: 'Location', width: '21%' },
                        { label: 'Owner', width: '15%' },
                        { label: 'Guests', width: '10%' },
                        { label: '', width: '6%' },
                      ].map((h, i) => (
                        <th
                          key={i}
                          style={{ width: h.width }}
                          className={`whitespace-nowrap px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 ${h.label === 'Guests' ? 'text-center' : 'text-left'}`}
                        >
                          {h.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEvents.map((event: any, i: number) => {
                      const isLast = i === paginatedEvents.length - 1
                      const hovered = hoveredRow === event.id
                      return (
                        <tr
                          key={event.id}
                          className="cursor-pointer"
                          onMouseEnter={() => setHoveredRow(event.id)}
                          onMouseLeave={() => setHoveredRow(null)}
                          onClick={() => handleEventClick(event)}
                        >
                          <td
                            className="align-top px-5 py-4 text-sm font-semibold leading-5 text-gray-900 transition-colors"
                            style={{
                              height: 52,
                              borderBottom: isLast ? 'none' : '1px solid #F3F3F0',
                              background: hovered ? '#F9F8FF' : '#fff',
                            }}
                          >
                            <span className="line-clamp-2 break-words">
                              {event.name || 'Untitled Event'}
                            </span>
                          </td>
                          <td
                            className="whitespace-nowrap align-top px-5 py-4 text-[13px] leading-5 text-gray-500 transition-colors"
                            style={{
                              height: 52,
                              borderBottom: isLast ? 'none' : '1px solid #F3F3F0',
                              background: hovered ? '#F9F8FF' : '#fff',
                            }}
                          >
                            {formatDate(event)}
                          </td>
                          <td
                            className="align-top px-5 py-4 transition-colors"
                            style={{
                              height: 52,
                              borderBottom: isLast ? 'none' : '1px solid #F3F3F0',
                              background: hovered ? '#F9F8FF' : '#fff',
                            }}
                          >
                            <span className="text-[13px] font-semibold leading-5 text-gray-500">
                              {getLocation(event)}
                            </span>
                          </td>
                          <td
                            className="whitespace-nowrap align-top px-5 py-4 text-[13px] leading-5 text-gray-500 transition-colors"
                            style={{
                              height: 52,
                              borderBottom: isLast ? 'none' : '1px solid #F3F3F0',
                              background: hovered ? '#F9F8FF' : '#fff',
                            }}
                          >
                            {getOwner(event)}
                          </td>
                          <td
                            className="whitespace-nowrap align-top px-5 py-4 text-center text-[13px] font-semibold leading-5 text-gray-900 transition-colors"
                            style={{
                              height: 52,
                              borderBottom: isLast ? 'none' : '1px solid #F3F3F0',
                              background: hovered ? '#F9F8FF' : '#fff',
                            }}
                          >
                            {getGuestCount(event)}
                          </td>
                          <td
                            className="align-top px-4 py-4 text-right transition-colors"
                            style={{
                              height: 52,
                              borderBottom: isLast ? 'none' : '1px solid #F3F3F0',
                              background: hovered ? '#F9F8FF' : '#fff',
                            }}
                          >
                            <span
                              className="text-base transition-colors"
                              style={{ color: hovered ? PURPLE : '#D1D5DB' }}
                            >
                              →
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>

              </div>
              {/* Footer with pagination */}
              <div className="flex items-center justify-between border-t border-[#F3F3F0] bg-[#FAFAF4] px-5 py-3">
                <span className="text-xs text-gray-400">
                  Showing {paginatedEvents.length} of {events.length} events
                </span>
                {totalPages > 1 && (
                  <Pagination className="mx-0 w-auto justify-end">
                    <PaginationContent>
                      {pageList.map((p, i) => (
                        <PaginationItem key={`${p}-${i}`}>
                          {p === 'ellipsis' ? (
                            <PaginationEllipsis />
                          ) : (
                            <PaginationLink
                              href="#"
                              isActive={p === safePage}
                              onClick={(e) => {
                                e.preventDefault()
                                if (p !== safePage) setCurrentPage(p)
                              }}
                            >
                              {p}
                            </PaginationLink>
                          )}
                        </PaginationItem>
                      ))}
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          aria-disabled={safePage === totalPages}
                          className={safePage === totalPages ? 'pointer-events-none opacity-50' : ''}
                          onClick={(e) => {
                            e.preventDefault()
                            if (safePage < totalPages) setCurrentPage(safePage + 1)
                          }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </div>
            </div>)
          )}
        </div>


        {hasCollectionsFeature ? (
          <CollectionsHomeTable
            collections={collections}
            isLoading={collectionsLoading}
            onCreateCollection={() => setIsCreateCollectionOpen(true)}
            onOpenCollection={handleCollectionClick}
          />
        ) : null}
      </div>

      {hasCollectionsFeature ? (
        <ShareableListDialog open={isCreateCollectionOpen} onOpenChange={setIsCreateCollectionOpen} initialStep="create" />
      ) : null}

      <AddEventToSpaceDialog startStep="create-event-form" open={isCreateEventOpen} onOpenChange={() => setIsCreateEventOpen(false)} shouldAddSpaceOnCreate={false} />
    </div>
  )
}
